import { EventEmitter } from 'node:events';
import { Session } from './session.js';
import { CdpClient, type ICdpClient } from './cdp.js';
import type { Config } from '../config.js';
import type { ToolCallEvent } from '../proxy/interceptor.js';
import { runRetention } from './storage.js';
import { metrics } from '../metrics.js';
import { planContext } from '../billing/context.js';
import { Feature } from '../billing/entitlements.js';
import type { SyncQueue } from '../sync/queue.js';

export interface RecordingEngineOptions {
  config: Config;
  /** Optional CDP client injection (for testing). */
  cdp?: ICdpClient;
  /** Optional sync queue for cloud sync (Pro/Team feature). */
  syncQueue?: SyncQueue;
}

/**
 * Recording engine that captures screenshots and action metadata.
 *
 * Listens for 'tool-call' events from the proxy interceptor.
 * On each capturable action, takes a screenshot via CDP,
 * saves it as PNG, and appends the action to the session manifest.
 *
 * Session lifecycle:
 * - Auto-starts on first tool call if no session is active
 * - Auto-ends after 60s of inactivity (debounced timer)
 * - Ends explicitly on shutdown
 *
 * Emits: 'session-start', 'session-end', 'action-recorded'
 */
export class RecordingEngine extends EventEmitter {
  private readonly config: Config;
  private readonly cdp: ICdpClient;
  private readonly capturableTools: Set<string>;
  private readonly syncQueue: SyncQueue | null;
  private currentSession: Session | null = null;
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private retentionInterval: ReturnType<typeof setInterval> | null = null;
  private readonly inactivityTimeoutMs = 60_000;

  constructor(options: RecordingEngineOptions) {
    super();
    this.config = options.config;
    this.cdp =
      options.cdp ??
      new CdpClient('127.0.0.1', this.config.chrome.debugPort);
    this.capturableTools = new Set(this.config.proxy.captureOn);
    this.syncQueue = options.syncQueue ?? null;
  }

  /** Start the engine: connect CDP and schedule retention. */
  async start(): Promise<void> {
    await this.cdp.connect();

    // Forward screencast frames from CDP to listeners (streaming server)
    this.cdp.on('screencast-frame', (data: Buffer) => {
      this.emit('screencast-frame', data);
    });

    // Run retention on startup
    this.doRetention();

    // Schedule hourly retention
    this.retentionInterval = setInterval(
      () => this.doRetention(),
      60 * 60 * 1000,
    );
  }

  /** Handle a tool call event from the interceptor. */
  async handleToolCall(event: ToolCallEvent): Promise<void> {
    // Only capture specified tools
    if (!this.capturableTools.has(event.tool)) return;

    // Auto-start session if needed
    if (!this.currentSession) {
      this.startSession();
    }

    // Reset inactivity timer
    this.resetInactivityTimer();

    // Capture screenshot and page info — failures must not crash the session
    let screenshot: Buffer | null = null;
    let pageInfo: { url: string; title: string } | null = null;
    let screenshotFailed = false;

    try {
      [screenshot, pageInfo] = await Promise.all([
        this.cdp.captureScreenshot(),
        this.cdp.getPageInfo(),
      ]);
      if (screenshot === null) {
        screenshotFailed = true;
      }
    } catch (err) {
      screenshotFailed = true;
      console.warn(
        `[browserforge] CDP capture error: ${err instanceof Error ? err.message : String(err)}`,
      );
    }

    if (screenshotFailed) {
      metrics.increment('screenshotsFailed');
    } else {
      metrics.increment('screenshotsCaptured');
    }
    metrics.increment('actionsRecorded');

    const entry = await this.currentSession!.recordAction({
      tool: event.tool,
      args: event.args,
      screenshot,
      pageUrl: pageInfo?.url ?? '',
      pageTitle: pageInfo?.title ?? '',
      ...(screenshotFailed ? { screenshotFailed: true } : {}),
    });

    this.emit('action-recorded', {
      sessionId: this.currentSession!.id,
      action: entry,
    });
  }

  /** Explicitly start a new session. */
  startSession(): Session {
    if (this.currentSession && !this.currentSession.isEnded) {
      this.endSession();
    }

    this.currentSession = new Session(this.config.storage.dir);
    this.resetInactivityTimer();

    metrics.increment('sessionsRecorded');
    metrics.increment('sessionsActive');

    this.emit('session-start', { sessionId: this.currentSession.id });

    // Start screencast if enabled
    if (this.config.screencast.enabled) {
      void this.cdp.startScreencast(
        this.config.screencast.quality,
        this.config.screencast.maxFps,
      );
    }

    return this.currentSession;
  }

  /** End the current session. */
  endSession(): void {
    if (!this.currentSession || this.currentSession.isEnded) return;

    // Stop screencast
    void this.cdp.stopScreencast();

    metrics.decrement('sessionsActive');

    this.clearInactivityTimer();
    const manifest = this.currentSession.end();
    this.emit('session-end', { sessionId: manifest.id, manifest });

    // Auto-enqueue for cloud sync if plan supports it
    if (this.syncQueue && planContext.hasFeature(Feature.CLOUD_SYNC)) {
      this.syncQueue.enqueue(manifest.id);
    }

    this.currentSession = null;
  }

  /** Graceful shutdown: end session, disconnect CDP, stop retention. */
  async shutdown(): Promise<void> {
    this.endSession();
    this.clearInactivityTimer();
    if (this.retentionInterval) {
      clearInterval(this.retentionInterval);
      this.retentionInterval = null;
    }
    await this.cdp.disconnect();
  }

  get activeSessionId(): string | null {
    return this.currentSession?.id ?? null;
  }

  get isRecording(): boolean {
    return this.currentSession !== null && !this.currentSession.isEnded;
  }

  private resetInactivityTimer(): void {
    this.clearInactivityTimer();
    this.inactivityTimer = setTimeout(() => {
      this.endSession();
    }, this.inactivityTimeoutMs);
  }

  private clearInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  private doRetention(): void {
    try {
      const result = runRetention(
        this.config.storage.dir,
        this.config.storage.maxSessions,
        this.config.storage.maxAgeDays,
      );
      if (result.deletedByAge > 0 || result.deletedByLimit > 0) {
        console.error(
          `[browserforge] Retention: deleted ${result.deletedByAge} expired, ${result.deletedByLimit} over limit`,
        );
      }
    } catch (err) {
      console.error(
        `[browserforge] Retention check failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }
}
